
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import backgroundImage from "../../Utils/assets/img/leads_no_data_found.png";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import {
  FaPlus,
  FaRegCommentDots,
  FaRegCalendarAlt,
  FaFlag,
  FaPaperclip,
  FaUser,
  FaTimes,
  FaChevronRight,
} from "react-icons/fa";
import {
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler";
import { formatDateInAlphabeticalMonth, getUpdatedFields } from "../../Utils/helpers/commonhelper";
import { Modal, Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { CreateProject } from "./CreateProject";

// Constants
const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent", color: "#f2394a" },
  { value: "high", label: "High", color: "#f2a93b" },
  { value: "normal", label: "Normal", color: "#4592f0" },
  { value: "low", label: "Low", color: "#8590a2" },
];
const STATUS_OPTIONS = [
  { value: "complete", label: "Complete", color: "#1bbf6b" },
  { value: "progress", label: "In Progress", color: "#f1c232" },
  { value: "pending", label: "Pending", color: "#87909e" },
  { value: "onhold", label: "On Hold", color: "#f2994a" },
  { value: "cancel", label: "Cancel", color: "#e5484d" },
];
const STATUS_ACTION_OPTIONS = [
  { value: "complete", label: "COMPLETE", color: "#1bbf6b" },
  { value: "progress", label: "IN PROGRESS", color: "#f1c232" },
  { value: "pending", label: "Pending", color: "#87909e" },
  { value: "onhold", label: "On Hold", color: "#f2994a" },
  { value: "cancel", label: "Cancel", color: "#e5484d" },
];

// Copy shown in the status-change confirm modal per selected status.
const STATUS_MODAL_COPY = {
  complete: {
    title: "Complete Task",
    message: "Are you sure you want to mark as complete this task?",
    needsReason: false,
  },
   pending: {
    title: "Pending Task",
    message: "Are you sure you want to mark this task as  Pending?",
  },
  progress: {
    title: "Progress Task",
    message: "Are you sure you want to mark this task as In-progress?",
  },
  onhold: {
    title: "On Hold Task",
    message: "Are you sure you want to mark this task as On-Hold?",
  },
  cancel: {
    title: "Close Task",
    message: "Are you sure you want to cancel this task?",
    needsReason: true,
    reasonPlaceholder: "Please provide a reason for cancellation",
  },
};

const AVATAR_COLORS = [
  "#7B68EE",
  "#4592f0",
  "#1bbf6b",
  "#f1c232",
  "#f2a93b",
  "#e5484d",
  "#00b8d9",
  "#d6409f",
  "#36b37e",
  "#8590a2",
];

const STAGE_COLORS = [
  "#ffff",
  "#ffe8cd",
  "#cbffe8",
  "#FFF3E0",
  "#E8F5E9",
  "#E0F7FA",
  "#FFF9E1",
  "#F1F8E9",
  "#EDE7F6",
  "#FBE9E7",
];

const getStageColor = (index) => STAGE_COLORS[index % STAGE_COLORS.length];

const getAvatarColor = (seed = "U") => {
  const str = String(seed || "U");
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "U";

const parseTaskAssignees = (task, assigneeOptions = []) => {
  if (!task) return [];
  if (Array.isArray(task.assignees) && task.assignees.length > 0) {
    return normalizeOwnerIdPayload(task.assignees, assigneeOptions);
  }
  return normalizeOwnerIdPayload(task.owner_id, assigneeOptions);
};

const normalizeOwnerIdPayload = (value, assigneeOptions = []) => {
  let list = value;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = list ? [list] : [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .filter((item) => item !== null && item !== undefined && item !== "")
    .map((item) => {
      if (item && typeof item === "object") {
        if (item.value === undefined) return item;
        if (item.label) return { value: item.value, label: item.label };
        const match = assigneeOptions.find(
          (o) => String(o.value) === String(item.value),
        );
        return match || { value: item.value, label: "User" };
      }
      const match = assigneeOptions.find(
        (o) => String(o.value) === String(item),
      );
      return match || { value: item, label: "User" };
    });
};

const toISO = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate(),
  ).padStart(2, "0")}`;
};

const hasRealDate = (d) => {
  if (!d) return false;
  const iso = toISO(d);
  return !!iso && iso !== "1970-01-01";
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Default start date = today, default end date = one day after start date.
const todayISO = () => toISO(new Date());
const tomorrowISO = () => toISO(addDays(new Date(), 1));

const isSameDay = (a, b) =>
  a && b && new Date(a).toDateString() === new Date(b).toDateString();

const isToday = (d) => d && isSameDay(d, new Date());

const nextWeekday = (from, targetDow) => {
  const x = startOfDay(from);
  const diff = (targetDow + 7 - x.getDay()) % 7 || 7;
  return addDays(x, diff);
};

const buildMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Avatar Component
const Avatar = ({ name, image, id, size = 24 }) =>
  image ? (
    <img
      src={image}
      alt={name || "User"}
      className="kb-avatar-img"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      className="kb-avatar"
      style={{
        width: size,
        height: size,
        background: getAvatarColor(id || name),
        fontSize: size <= 22 ? 9 : 10,
      }}
    >
      {getInitials(name)}
    </div>
  );

// Popover Component – with high z-index and no visibility condition
const Popover = ({ onClose, children, style, className = "", anchorRect }) => {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!anchorRect || !popoverRef.current) return;
    const popoverEl = popoverRef.current;
    const popoverWidth = popoverEl.offsetWidth;
    const popoverHeight = popoverEl.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = anchorRect.bottom + 6;
    let left = anchorRect.left;

    if (top + popoverHeight > viewportHeight - 10) {
      top = anchorRect.top - popoverHeight - 6;
    }
    if (top < 10) top = 10;

    if (left + popoverWidth > viewportWidth - 10) {
      left = viewportWidth - popoverWidth - 10;
    }
    if (left < 10) left = 10;

    setPosition({ top, left });
  }, [anchorRect]);

  useEffect(() => {
    const handleOutsideMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleOutsideMouseDown, true);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideMouseDown, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  return createPortal(
    <>
      <div className="kb-popover-backdrop" />
      <div
        ref={popoverRef}
        className={`kb-popover ${className}`}
        style={{
          ...style,
          top: position.top,
          left: position.left,
          position: "fixed",
          zIndex: 9999, // high z-index
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body,
  );
};

// Assignee Popover
const AssigneePopover = ({
  options,
  selected,
  onChange,
  onClose,
  anchorRect,
}) => {
  const [query, setQuery] = useState("");
  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase()),
  );
  const isSelected = (opt) => selected.some((s) => s.value === opt.value);
  const toggle = (opt) => {
    if (isSelected(opt))
      onChange(selected.filter((s) => s.value !== opt.value));
    else onChange([...selected, opt]);
  };

  return (
    <Popover
      onClose={onClose}
      className="kb-assignee-popover"
      anchorRect={anchorRect}
    >
      <input
        autoFocus
        className="kb-popover-search"
        placeholder="Search or enter email..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="kb-popover-section-label">People</div>
      <div className="kb-popover-list">
        {filtered.length === 0 && (
          <div className="kb-popover-empty">No matches</div>
        )}
        {filtered.map((opt) => (
          <div
            key={opt.value}
            className={`kb-popover-row ${isSelected(opt) ? "kb-popover-row-active" : ""}`}
            onClick={() => toggle(opt)}
          >
            <Avatar name={opt.label} id={opt.value} size={22} />
            <span className="fs-13 flex-grow-1">{opt.label}</span>
            {isSelected(opt) && <span className="kb-check">✓</span>}
          </div>
        ))}
      </div>
    </Popover>
  );
};

// Date Popover
const DatePopover = ({
  startDate,
  dueDate,
  activeField,
  onChange,
  onClose,
  anchorRect,
}) => {
  const [field, setField] = useState(activeField || "due_date");
  const seed = (field === "start_date" ? startDate : dueDate) || new Date();
  const [viewYear, setViewYear] = useState(new Date(seed).getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date(seed).getMonth());

  // Tracks a start_date picked in this same popover session, so the due_date
  // tab can immediately disable anything before it without waiting on the
  // parent's prop update.
  const [localSelection, setLocalSelection] = useState({
    start_date: null,
    due_date: null,
  });

  const applyDate = (d) => {
    setLocalSelection((prev) => ({ ...prev, [field]: d }));
    onChange({ [field]: toISO(d) });
    if (field === "start_date") {
      // Keep the popover open and guide the user straight to picking the end date.
      setField("due_date");
    } else {
      onClose();
    }
  };

  // Due date can never be picked before the start date.
  const effectiveStartDate = localSelection.start_date || startDate;
  const isBeforeStartDate = (d) =>
    field === "due_date" &&
    effectiveStartDate &&
    startOfDay(d).getTime() <
      startOfDay(new Date(effectiveStartDate)).getTime();

  const quickOptions = [
    {
      label: "Today",
      sub: MONTH_NAMES[new Date().getMonth()].slice(0, 3),
      get: () => new Date(),
    },
    { label: "Tomorrow", sub: "", get: () => addDays(new Date(), 1) },
    { label: "This weekend", sub: "", get: () => nextWeekday(new Date(), 6) },
    { label: "Next week", sub: "", get: () => nextWeekday(new Date(), 1) },
    {
      label: "Next weekend",
      sub: "",
      get: () => addDays(nextWeekday(new Date(), 6), 7),
    },
    { label: "2 weeks", sub: "", get: () => addDays(new Date(), 14) },
    { label: "4 weeks", sub: "", get: () => addDays(new Date(), 28) },
  ];

  const grid = buildMonthGrid(viewYear, viewMonth);
  const selectedForField =
    localSelection[field] || (field === "start_date" ? startDate : dueDate);

  const changeMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
    if (m > 11) {
      m = 0;
      y += 1;
    }
    setViewMonth(m);
    setViewYear(y);
  };

  return (
    <Popover
      onClose={onClose}
      className="kb-date-popover"
      anchorRect={anchorRect}
    >
      <div className="kb-date-tabs">
        <button
          type="button"
          className={`kb-date-tab ${field === "start_date" ? "kb-date-tab-active" : ""}`}
          onClick={() => setField("start_date")}
        >
          {localSelection.start_date || startDate
            ? formatDateInAlphabeticalMonth(
                localSelection.start_date || startDate,
              )
            : "Start date"}
        </button>
        <span className="kb-muted">→</span>
        <button
          type="button"
          className={`kb-date-tab ${field === "due_date" ? "kb-date-tab-active" : ""}`}
          onClick={() => setField("due_date")}
        >
          {localSelection.due_date || dueDate
            ? formatDateInAlphabeticalMonth(localSelection.due_date || dueDate)
            : "Due date"}
        </button>
         <span
          className="tl-date-popover-close"
          title="Close"
          onClick={onClose}
        >
          <FaTimes size={12} />
        </span>
      </div>

      <div className="kb-date-body">
        <div className="kb-date-quick">
          {quickOptions.map((q) => {
            const disabled = isBeforeStartDate(q.get());
            return (
              <div
                key={q.label}
                className={`kb-date-quick-row${disabled ? " kb-date-quick-row-disabled" : ""}`}
                onClick={() => !disabled && applyDate(q.get())}
              >
                <span>{q.label}</span>
                <span className="kb-muted fs-11">
                  {toISO(q.get()) === toISO(new Date())
                    ? "Today"
                    : q.get().toLocaleDateString(undefined, { weekday: "short" })}
                </span>
              </div>
            );
          })}
          <div className="kb-date-quick-row kb-date-recurring">
            <span>Set Recurring</span>
            <FaChevronRight size={10} />
          </div>
        </div>

        <div className="kb-date-calendar">
          <div className="kb-date-cal-header">
            <span className="fw-semibold fs-13">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <div className="d-flex gap-2">
              <span className="kb-cal-nav" onClick={() => changeMonth(-1)}>
                ‹
              </span>
              <span className="kb-cal-nav" onClick={() => changeMonth(1)}>
                ›
              </span>
            </div>
          </div>
          <div className="kb-date-cal-grid kb-date-cal-dow">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="kb-date-cal-grid">
            {grid.map((d, i) => {
              const disabled = d && isBeforeStartDate(d);
              return (
                <span
                  key={i}
                  className={[
                    "kb-cal-cell",
                    !d && "kb-cal-cell-empty",
                    d && isToday(d) && "kb-cal-cell-today",
                    d &&
                      selectedForField &&
                      isSameDay(d, selectedForField) &&
                      "kb-cal-cell-selected",
                    disabled && "kb-cal-cell-disabled",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => d && !disabled && applyDate(d)}
                >
                  {d ? d.getDate() : ""}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </Popover>
  );
};

// Option Popover (for priority and status)
const OptionPopover = ({
  options,
  onSelect,
  taskGrant,
  onClose,
  allowClear,
  anchorRect,
}) => (
  <Popover
    onClose={onClose}
    className="kb-option-popover"
    anchorRect={anchorRect}
  >
    {options.map((opt) => (
      <div
        key={opt.value}
        className="kb-popover-row"
        onClick={() => onSelect(opt)}
      >
        <span className="kb-dot" style={{ background: opt.color }} />
        <span className="fs-13">{opt.label}</span>
      </div>
    ))}
    {allowClear && (
      <div className="kb-popover-row kb-muted" onClick={() => onSelect(null)}>
        <span className="fs-13">Clear</span>
      </div>
    )}
  </Popover>
);

const ChangeStatusModal = ({ status, submitting, onCancel, onSubmit,taskGrant }) => {
  const copy = STATUS_MODAL_COPY[status?.value] || STATUS_MODAL_COPY.complete;
  const [reason, setReason] = useState("");

  return createPortal(
    <div className="kb-status-modal-backdrop" onMouseDown={onCancel}>
      <div
        className="kb-status-modal"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
      >
        <div className="kb-status-modal-header">
          <span>{copy.title}</span>
          <span className="kb-status-modal-close" onClick={onCancel}>
            <FaTimes size={14} />
          </span>
        </div>
        <div className="kb-status-modal-body">
          <p className="kb-status-modal-message">{copy.message}</p>
          {copy.needsReason && (
            <>
              <label className="kb-status-modal-label">Reason</label>
              <textarea
                autoFocus
                className="kb-status-modal-textarea"
                placeholder={copy.reasonPlaceholder}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          )}
        </div>
        <div className="kb-status-modal-footer">
          <button
            className="kb-status-modal-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="kb-status-modal-submit"
            onClick={() => onSubmit(copy.needsReason ? reason : undefined)}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Draggable Task Card – using onPointerDown with preventDefault
const KanbanTaskCard = ({
  task,
  taskIndex,
  onTaskClick,
  onEdit,
  onDelete,
  taskGrant,
  onStatusChange,
  assigneeOptions,
  onUpdateField,
  cardColor,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: { task },
  });

  const [openField, setOpenField] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [localAssignees, setLocalAssignees] = useState([]);
  const [localStartDate, setLocalStartDate] = useState(
    hasRealDate(task.start_date) ? task.start_date : null,
  );
  const [localDueDate, setLocalDueDate] = useState(
    hasRealDate(task.end_date)
      ? task.end_date
      : hasRealDate(task.due_date)
        ? task.due_date
        : null,
  );
  const [localPriority, setLocalPriority] = useState(task.priority || null);
  const [statusAction, setStatusAction] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: cardColor || "#ffffff",
    // Leave a clean gap behind while the DragOverlay clone follows the pointer
    opacity: isDragging ? 0 : 1,
    pointerEvents: isDragging ? "none" : "auto",
  };
  const priorityColor =
    PRIORITY_OPTIONS.find((p) => p.value === (localPriority || task.priority))
      ?.color || "#8590a2";
  const statusColor =
    STATUS_OPTIONS.find((s) => s.value === task.task_status)?.color ||
    "#87909e";
  const statusLabel =
  
    STATUS_OPTIONS.find((s) => s.value === task.task_status )?.label || "TO DO";
  const isOverdue =
    hasRealDate(localDueDate) &&
    new Date(localDueDate) < new Date() &&
    task.task_status !== "complete";

  const getDueDateLabel = (dateStr) => {
    if (!hasRealDate(dateStr)) return null;
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return formatDateInAlphabeticalMonth(dateStr);
  };
  const getDateRangeLabel = () => {
    const start = hasRealDate(localStartDate) ? localStartDate : null;
    const due = hasRealDate(localDueDate) ? localDueDate : null;
    if (start && due) {
      //${formatDateInAlphabeticalMonth(start)} →
      return `${getDueDateLabel(due)}`;
    }
    if (due) return getDueDateLabel(due);
    if (start) return formatDateInAlphabeticalMonth(start);
    return "Set date";
  };

  // Updated handlers with onPointerDown + preventDefault
  const handleAssigneeClick = (e) => {
    if(!taskGrant?.isEdit) return toast.warning("You are not authorized for this action!")
    e.stopPropagation();
    e.preventDefault();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setOpenField("assignee");
  };

  const handleDateClick = (e) => {
    if(!taskGrant?.isEdit) return toast.warning("You are not authorized for this action!")
    e.stopPropagation();
    e.preventDefault();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setOpenField("date");
  };

  const handlePriorityClick = (e) => {
    if(!taskGrant?.isEdit) return toast.warning("You are not authorized for this action!")
    e.stopPropagation();
    e.preventDefault();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setOpenField("priority");
  };

  const handleStatusClick = (e) => {
    if(!taskGrant?.isEdit) return toast.warning("You are not authorized for this action!")
    e.stopPropagation();
    e.preventDefault();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setOpenField("status");
  };

  const closePopover = () => {
    setOpenField(null);
    setAnchorRect(null);
  };

  const handleStatusSubmit = async (reason) => {
    if (!statusAction) return;
    setStatusSubmitting(true);
    try {
      const ok = await onStatusChange?.(task, statusAction.value, reason);
      if (ok !== false) setStatusAction(null);
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleAssigneeChange = async (selected) => {
    setLocalAssignees(selected);
    closePopover();
    if (onUpdateField) {
      await onUpdateField(task.id, { assignees: selected });
    }
  };

  const handleDateChange = async (patch) => {
    if (patch.start_date !== undefined) {
      const newStart = patch.start_date;
      // Due date can never sit before the (possibly new) start date; if it
      // does, or if there isn't one yet, default it to start date + 1 day.
      const newDue =
        hasRealDate(localDueDate) &&
        new Date(localDueDate) >= new Date(newStart)
          ? localDueDate
          : toISO(addDays(newStart, 1));
      setLocalStartDate(newStart);
      setLocalDueDate(newDue);
      if (onUpdateField) {
        await onUpdateField(task.id, {
          start_date: newStart,
          due_date: newDue,
        });
      }
      return;
    }
    if (patch.due_date !== undefined) {
      // If no start date was ever picked, default it to today.
      const resolvedStart = hasRealDate(localStartDate)
        ? localStartDate
        : todayISO();
      setLocalDueDate(patch.due_date);
      setLocalStartDate(resolvedStart);
      if (onUpdateField) {
        await onUpdateField(task.id, {
          start_date: resolvedStart,
          due_date: patch.due_date,
        });
      }
      closePopover();
    }
  };

  const handlePriorityChange = async (opt) => {
    setLocalPriority(opt?.value || null);
    closePopover();
    if (onUpdateField) {
      await onUpdateField(task.id, { priority: opt?.value || null });
    }
  };

  const assigneeDisplay =
    localAssignees.length > 0
      ? localAssignees
      : parseTaskAssignees(task, assigneeOptions);

  // Truncates text to a max word count, appending an ellipsis when clipped
 const truncateCharacters = (text, maxChars = 100) => {
  if (!text) return "";
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars)}...`;
};
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="kb-task-card mb-2"
    >
      <div className="kb-task-card-header">
        <span
          className="kb-task-title kb-task-title-clickable fs-12 fw-400 text-muted "
          onClick={(e) => {
            if(!taskGrant?.isView) return toast.warning("You are not authorized for this action!")
            e.stopPropagation();
            onTaskClick && onTaskClick(task);
          }}
        >
           {truncateCharacters(task.task_name,30)}
          {/* {task.task_name} */}
        </span>
       
      </div>

      {/* {task.description && (
        <div className="kb-task-description">
          {task.description.length > 60 ? `${task.description.slice(0, 60)}...` : task.description}
        </div>
      )} */}

      <div className="kb-task-meta">
        <span
          className="kb-task-priority"
          style={{ color: priorityColor, cursor: "pointer" }}
          onPointerDown={handlePriorityClick}
        >
          <FaFlag size={12} />
          <span>
            {PRIORITY_OPTIONS.find(
              (p) => p.value === (localPriority || task.priority),
            )?.label || "Normal"}
          </span>
        </span>
        {openField === "priority" && (
          <OptionPopover
            options={PRIORITY_OPTIONS}
            allowClear
            onSelect={handlePriorityChange}
            taskGrant={taskGrant}
            onClose={closePopover}
            anchorRect={anchorRect}
          />
        )}

        <span
          className={`kb-task-due ${isOverdue ? "kb-task-overdue" : ""}`}
          style={{ cursor: "pointer" }}
          onPointerDown={handleDateClick}
        >
          <FaRegCalendarAlt size={12} />
          <span>{getDateRangeLabel()}</span>
        </span>
        {openField === "date" && (
          <DatePopover
            startDate={hasRealDate(localStartDate) ? localStartDate : null}
            dueDate={hasRealDate(localDueDate) ? localDueDate : null}
            activeField={
              hasRealDate(localStartDate) ? "due_date" : "start_date"
            }
            onChange={handleDateChange}
            onClose={closePopover}
            anchorRect={anchorRect}
          />
        )}

        <span
          className="kb-task-status"
          style={{
            borderColor: statusColor,
            color: statusColor,
            cursor: "pointer",
          }}
          onPointerDown={handleStatusClick}
        >
          {statusLabel}
        </span>
        {openField === "status" && (
          <OptionPopover
            options={STATUS_ACTION_OPTIONS.filter((opt) => {
              return opt.value !== task?.task_status;
            })}
            onSelect={(opt) => {
              closePopover();
              setStatusAction(opt);
            }}
            onClose={closePopover}
            anchorRect={anchorRect}
          />
        )}
        {statusAction && (
          <ChangeStatusModal
            status={statusAction}
            taskGrant={taskGrant}
            submitting={statusSubmitting}
            onCancel={() => setStatusAction(null)}
            onSubmit={handleStatusSubmit}
          />
        )}
      </div>

      <div className="kb-task-footer">
        <div
          className="kb-task-assignees"
          style={{ cursor: "pointer" }}
          onPointerDown={handleAssigneeClick}
        >
          {assigneeDisplay.length > 0 ? (
            assigneeDisplay
              .slice(0, 3)
              .map((assignee, idx) => (
                <Avatar
                  key={idx}
                  name={assignee.name || assignee.label}
                  id={assignee.id || assignee.value}
                  size={22}
                />
              ))
          ) : (
            <span className="kb-no-assignee">
              <FaUser size={12} className="me-1" /> No assignee
            </span>
          )}
          {assigneeDisplay.length > 3 && (
            <span className="kb-more-assignees">
              +{assigneeDisplay.length - 3}
            </span>
          )}
        </div>
        {openField === "assignee" && (
          <AssigneePopover
            options={assigneeOptions}
            selected={assigneeDisplay}
            onChange={handleAssigneeChange}
            onClose={closePopover}
            anchorRect={anchorRect}
          />
        )}
        <div className="kb-task-stats">
          {task.comment_count > 0 && (
            <span className="kb-task-stat">
              <FaRegCommentDots size={12} />
              {task.comment_count}
            </span>
          )}
          {task.attachment_count > 0 && (
            <span className="kb-task-stat">
              <FaPaperclip size={12} />
              {task.attachment_count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const KanbanTaskCardPreview = ({ task, cardColor, assigneeOptions ,taskGrant }) => {
  const priorityColor =
    PRIORITY_OPTIONS.find((p) => p.value === task.priority)?.color || "#8590a2";
  const statusColor =
    STATUS_OPTIONS.find((s) => s.value === task.task_status)?.color ||
    "#87909e";
  const statusLabel =
    STATUS_OPTIONS.find((s) => s.value === task.task_status)?.label || "TO DO";
  const assignees = parseTaskAssignees(task, assigneeOptions);
  const dueDate = hasRealDate(task.end_date)
    ? task.end_date
    : hasRealDate(task.due_date)
      ? task.due_date
      : null;

  return (
    <div
      className="kb-task-card kb-task-card-overlay"
      style={{ backgroundColor: cardColor || "#ffffff" }}
    >
      <div className="kb-task-card-header">
        <span className="kb-task-title">{task.task_name}</span>
      </div>
      {task.description && (
        <div className="kb-task-description">
          {task.description.length > 60
            ? `${task.description.slice(0, 60)}...`
            : task.description}
        </div>
      )}
      <div className="kb-task-meta">
        <span className="kb-task-priority" style={{ color: priorityColor }}>
          <FaFlag size={12} />
          <span>
            {PRIORITY_OPTIONS.find((p) => p.value === task.priority)?.label ||
              "Normal"}
          </span>
        </span>
        <span className="kb-task-due">
          <FaRegCalendarAlt size={12} />
          <span>
            {dueDate ? formatDateInAlphabeticalMonth(dueDate) : "Set date"}
          </span>
        </span>
        <span
          className="kb-task-status"
          style={{ borderColor: statusColor, color: statusColor }}
        >
          {statusLabel}
        </span>
      </div>
      <div className="kb-task-footer">
        <div className="kb-task-assignees">
          {assignees.length > 0 ? (
            assignees
              .slice(0, 3)
              .map((assignee, idx) => (
                <Avatar
                  key={idx}
                  name={assignee.name || assignee.label}
                  id={assignee.id || assignee.value}
                  size={22}
                />
              ))
          ) : (
            <span className="kb-no-assignee">
              <FaUser size={12} className="me-1" /> No assignee
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Droppable Column – with quick-add toolbar using onPointerDown
const KanbanColumn = ({
  column,
  tasks,
  onQuickAddTask,
  onTaskClick,
  onEdit,
  taskGrant,
  onDelete,
  onStatusChange,
  onUpdateField,
  assigneeOptions,
  color,
  cardColor,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { column },
  });
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddValue, setQuickAddValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [quickAddAssignee, setQuickAddAssignee] = useState([]);
  const [quickAddStartDate, setQuickAddStartDate] = useState(null);
  const [quickAddDueDate, setQuickAddDueDate] = useState(null);
  const [quickAddPriority, setQuickAddPriority] = useState(null);
  const [quickAddOpenField, setQuickAddOpenField] = useState(null);
  const [quickAddAnchorRect, setQuickAddAnchorRect] = useState(null);
  const quickAddRef = useRef(null);
  useEffect(() => {
    if (quickAddOpen && quickAddRef.current) quickAddRef.current.focus();
  }, [quickAddOpen]);
  const closeQuickAdd = () => {
    setQuickAddOpen(false);
    setQuickAddValue("");
    setQuickAddAssignee([]);
    setQuickAddStartDate(null);
    setQuickAddDueDate(null);
    setQuickAddPriority(null);
    setQuickAddOpenField(null);
    setQuickAddAnchorRect(null);
  };
  const submitQuickAdd = async () => {
    const name = quickAddValue.trim();
    if (!name || submitting) {
      if (!name) closeQuickAdd();
      return;
    }
    setSubmitting(true);
    try {
      await onQuickAddTask(
        column.id,
        name,
        quickAddAssignee,
        quickAddStartDate,
        quickAddDueDate,
        quickAddPriority,
      );
      setQuickAddValue("");
      setQuickAddAssignee([]);
      setQuickAddStartDate(null);
      setQuickAddDueDate(null);
      setQuickAddPriority(null);
      if (quickAddRef.current) quickAddRef.current.focus();
    } finally {
      setSubmitting(false);
    }
  };
  const handleQuickAddKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitQuickAdd();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeQuickAdd();
    }
  };
  // Updated quick-add toolbar handlers with onPointerDown
  const handleQuickAssigneeClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setQuickAddAnchorRect(rect);
    setQuickAddOpenField("assignee");
  };
  const handleQuickDateClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setQuickAddAnchorRect(rect);
    setQuickAddOpenField("date");
  };
  const handleQuickPriorityClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setQuickAddAnchorRect(rect);
    setQuickAddOpenField("priority");
  };

  const closeQuickPopover = () => {
    setQuickAddOpenField(null);
    setQuickAddAnchorRect(null);
  };

  const priorityColor =
    PRIORITY_OPTIONS.find((p) => p.value === quickAddPriority)?.color ||
    "#8590a2";
  const dueDateLabel = (() => {
    const dueLabel = quickAddDueDate
      ? isToday(quickAddDueDate)
        ? "Today"
        : formatDateInAlphabeticalMonth(quickAddDueDate)
      : null;
    const startLabel = quickAddStartDate
      ? formatDateInAlphabeticalMonth(quickAddStartDate)
      : null;
    if (startLabel && dueLabel) return `${startLabel} → ${dueLabel}`;
    return dueLabel || startLabel;
  })();

  return (
    <div className="kb-column">
      <div className="kb-column-header">
        <div className="kb-column-title">
          <span
            className="kb-column-dot"
            style={{ background: color || "#87909e" }}
          />
          <span className="kb-column-name">{column.name}</span>
          <span className="kb-column-count">{tasks.length}</span>
        </div>
        <button
          className="kb-column-add-btn"
          onClick={() => {if(!taskGrant?.isCreate) return toast.warning("You are not authorized for this action!")
            setQuickAddOpen(true)
          }}
        >
          <FaPlus size={12} />
        </button>
      </div>
      <div
        ref={setNodeRef}
        className={`kb-column-tasks ${isOver ? "kb-drag-over" : ""}`}
      >
        <SortableContext
          items={tasks.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map((task, index) => (
            <KanbanTaskCard
              key={task.id}
              task={task}
              taskIndex={index}
              onTaskClick={onTaskClick}
              onEdit={onEdit}
              onDelete={onDelete}
              taskGrant={taskGrant}
              onStatusChange={onStatusChange}
              onUpdateField={onUpdateField}
              assigneeOptions={assigneeOptions}
              cardColor={cardColor}
            />
          ))}
        </SortableContext>
        {tasks.length === 0 && !quickAddOpen && (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{
              flex: 1,
              color: "#6c757d",
              padding: "16px",
              border: isOver ? "2px dashed #4da4ff" : "2px dashed #dee2e6",
              borderRadius: "4px",
              background: "#fff",
            }}
          >
            {isOver ? (
              "Drop lead here"
            ) : (
              <>
                <img
                  src={backgroundImage || "/placeholder.svg"}
                  alt=""
                  style={{ width: "70%" }}
                />

                <p
                  style={{
                    color: "#038fed",
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                  onClick={() => setQuickAddOpen(true)}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Task
                </p>
              </>
            )}
          </div>
        )}

        {quickAddOpen ? (
          <div className="kb-quick-add">
            <textarea
              ref={quickAddRef}
              className="kb-quick-add-input"
              placeholder="Enter a title for this task..."
              rows={2}
              value={quickAddValue}
              disabled={submitting}
              onChange={(e) => setQuickAddValue(e.target.value)}
              onKeyDown={handleQuickAddKeyDown}
            />
            <div className="kb-quick-add-toolbar">
              {/* Assignee – using onPointerDown */}
              <span
                className="kb-quick-add-tool"
                onPointerDown={handleQuickAssigneeClick}
                title="Add assignee"
              >
                <FaUser size={12} />
                {quickAddAssignee.length > 0 && (
                  <span className="kb-quick-add-tool-label">
                    {quickAddAssignee.map((a) => a.label).join(", ")}
                  </span>
                )}
              </span>
              {quickAddOpenField === "assignee" && (
                <AssigneePopover
                  options={assigneeOptions}
                  selected={quickAddAssignee}
                  onChange={(sel) => {
                    setQuickAddAssignee(sel);
                    closeQuickPopover();
                  }}
                  onClose={closeQuickPopover}
                  anchorRect={quickAddAnchorRect}
                />
              )}

              {/* Date – using onPointerDown */}
              <span
                className="kb-quick-add-tool"
                onPointerDown={handleQuickDateClick}
                title="Add start & due date"
              >
                <FaRegCalendarAlt size={12} />
                {dueDateLabel && (
                  <span className="kb-quick-add-tool-label">
                    {dueDateLabel}
                  </span>
                )}
              </span>
              {quickAddOpenField === "date" && (
                <DatePopover
                  startDate={quickAddStartDate}
                  dueDate={quickAddDueDate}
                  activeField={quickAddStartDate ? "due_date" : "start_date"}
                  onChange={(patch) => {
                    if (patch.start_date !== undefined) {
                      const newStart = patch.start_date;
                      // Keep due date on/after the newly picked start date.
                      const newDue =
                        quickAddDueDate &&
                        new Date(quickAddDueDate) >= new Date(newStart)
                          ? quickAddDueDate
                          : toISO(addDays(newStart, 1));
                      setQuickAddStartDate(newStart);
                      setQuickAddDueDate(newDue);
                    }
                    if (patch.due_date !== undefined) {
                      setQuickAddDueDate(patch.due_date);
                      closeQuickPopover();
                    }
                  }}
                  onClose={closeQuickPopover}
                  anchorRect={quickAddAnchorRect}
                />
              )}

              {/* Priority – using onPointerDown */}
              <span
                className="kb-quick-add-tool"
                onPointerDown={handleQuickPriorityClick}
                title="Set priority"
                style={{ color: priorityColor }}
              >
                <FaFlag size={12} />
                {quickAddPriority && (
                  <span className="kb-quick-add-tool-label">
                    {
                      PRIORITY_OPTIONS.find((p) => p.value === quickAddPriority)
                        ?.label
                    }
                  </span>
                )}
              </span>
              {quickAddOpenField === "priority" && (
                <OptionPopover
                  options={PRIORITY_OPTIONS}
                  allowClear
                  onSelect={(opt) => {
                    setQuickAddPriority(opt?.value || null);
                    closeQuickPopover();
                  }}
                  onClose={closeQuickPopover}
                  anchorRect={quickAddAnchorRect}
                />
              )}
            </div>
            <div className="kb-quick-add-actions">
              <button
                type="button"
                className="kb-quick-add-submit"
                onClick={submitQuickAdd}
                disabled={submitting || !quickAddValue.trim()}
              >
                {submitting ? "Adding..." : "Add Task"}
              </button>
              <button
                type="button"
                className="kb-quick-add-cancel"
                onClick={closeQuickAdd}
              >
                <FaTimes size={13} />
              </button>
            </div>
          </div>
        ) : (
          tasks.length > 0 && (
            <div
              className="kb-add-task-bottom"
              onClick={() => setQuickAddOpen(true)}
            >
              <FaPlus size={12} />
              <span>Add Task</span>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// Add Task Modal (full form)
const AddTaskModal = ({
  show,
  onHide,
  onSubmit,
  taskGrant,
  columnId,
  assigneeOptions,
  projectData,
  headers,
  extras,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      task_name: "",
      description: "",
      priority: "normal",
      due_date: "",
      assignees: [],
    },
  });

  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const payload = {
        project_id: projectData?.id,
        project_stage_id: columnId,
        task_name: data.task_name,
        description: data.description || "",
        task_status: "open",
        priority: data.priority,
        owner_id: normalizeOwnerIdPayload(data.assignees, assigneeOptions),
        start_date: todayISO(),
        tags:[],
        end_date: data.due_date || tomorrowISO(),
      };

      const res = await PostRequest(
        "project/createTask",
        payload,
        false,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        toast.error(res?.message || "Could not create task");
        return;
      }
      toast.success("Task created successfully");
      onSubmit(res.data);
      reset();
      onHide();
    } catch (error) {
      console.error(error);
      toast.error("Could not create task");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="kb-add-task-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Add Task</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Task Name</Form.Label>
            <Controller
              name="task_name"
              control={control}
              rules={{ required: "Task name is required" }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  placeholder="Enter task name..."
                  isInvalid={!!errors.task_name}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.task_name?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  as="textarea"
                  rows={3}
                  placeholder="Add description..."
                />
              )}
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <Form.Select {...field}>
                      {PRIORITY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Due Date</Form.Label>
                <Controller
                  name="due_date"
                  control={control}
                  render={({ field }) => (
                    <Form.Control {...field} type="date" min={todayISO()} />
                  )}
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Assignees</Form.Label>
            <Controller
              name="assignees"
              control={control}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  multiple
                  value={field.value?.map((a) => a.value) || []}
                  onChange={(e) => {
                    const selected = Array.from(
                      e.target.selectedOptions,
                      (option) =>
                        assigneeOptions.find((a) => a.value === option.value),
                    ).filter(Boolean);
                    field.onChange(selected);
                  }}
                >
                  {assigneeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Task"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// Edit Task Modal
const EditTaskModal = ({
  show,
  onHide,
  task,
  onSubmit,
  taskGrant,
  assigneeOptions,
  headers,
  extras,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Default start date = today (when the task has none), default due date =
  // one day after start (when the task has none).
  const resolvedStartDate = task?.start_date || todayISO();
  const defaultDueDate = hasRealDate(task?.end_date)
    ? task.end_date
    : hasRealDate(task?.due_date)
      ? task.due_date
      : toISO(addDays(resolvedStartDate, 1));
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      task_name: task?.task_name || "",
      description: task?.description || "",
      priority: task?.priority || "normal",
      due_date: defaultDueDate,
      assignees: parseTaskAssignees(task, assigneeOptions),
      task_status: task?.task_status || "open",
    },
  });
  useEffect(() => {
    if (task) {
      const startForReset = task.start_date || todayISO();
      reset({
        task_name: task.task_name || "",
        description: task.description || "",
        priority: task.priority || "normal",
        due_date: hasRealDate(task.end_date)
          ? task.end_date
          : hasRealDate(task.due_date)
            ? task.due_date
            : toISO(addDays(startForReset, 1)),
        assignees: parseTaskAssignees(task, assigneeOptions),
        task_status: task.task_status || "open",
      });
    }
  }, [task, reset, assigneeOptions]);
  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    const parseIfNeeded = (data) => {
        if (typeof data === "string") {
          try {
            return JSON.parse(data);
          } catch (e) {
            return data;
          }
        }
        return data;
      };
    try {
      const payload = {
        task_id: task?.id,
        project_id: task?.project_id,
        stage_id: task?.project_stage_id,
        task_name: data.task_name,
        description: data.description || "",
        task_status: data.task_status,
        priority: data.priority,
        owner_id: normalizeOwnerIdPayload(data.assignees, assigneeOptions),
        start_date: resolvedStartDate,
        end_date: data.due_date || toISO(addDays(resolvedStartDate, 1)),
        tags:parseIfNeeded(task?.tags)||[],
      };
      payload.updating_field=getUpdatedFields(task,payload)
      const res = await PutRequest(
        "project/updateStageTask",
        payload,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        toast.error(res?.message || "Could not update task");
        return;
      }
      toast.success("Task updated successfully");
      onSubmit(res.data);
      onHide();
    } catch (error) {
      console.error(error);
      toast.error("Could not update task");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="kb-add-task-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Task</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Task Name</Form.Label>
            <Controller
              name="task_name"
              control={control}
              rules={{ required: "Task name is required" }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  placeholder="Enter task name..."
                  isInvalid={!!errors.task_name}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.task_name?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  as="textarea"
                  rows={3}
                  placeholder="Add description..."
                />
              )}
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <Form.Select {...field}>
                      {PRIORITY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Controller
                  name="task_status"
                  control={control}
                  render={({ field }) => (
                    <Form.Select {...field}>
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Due Date</Form.Label>
                <Controller
                  name="due_date"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="date"
                      min={resolvedStartDate}
                    />
                  )}
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Assignees</Form.Label>
            <Controller
              name="assignees"
              control={control}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  multiple
                  value={field.value?.map((a) => a.value) || []}
                  onChange={(e) => {
                    const selected = Array.from(
                      e.target.selectedOptions,
                      (option) =>
                        assigneeOptions.find((a) => a.value === option.value),
                    ).filter(Boolean);
                    field.onChange(selected);
                  }}
                >
                  {assigneeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Update Task"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
// Main Kanban Board Component
export const KanbanBoard = ({
  projectData,
  headers,
  extras,
  customerId,
  customer,
  onTaskClick,
  onDataChange,
  taskGrant,
  fetchProjectList,
  refetch,
}) => {
  const [stages, setStages] = useState([]);
  const [tasksByStage, setTasksByStage] = useState({});
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  const [activeTask, setActiveTask] = useState(null);
  const [activeCardColor, setActiveCardColor] = useState(null);
    const [showProjectCanvas, setProjectOffcanvas] = useState(false);
  const [project_id, setProject_id] = useState();
    const [editedMode, setEditMode] = useState(false);
  const originalContainerRef = useRef(null);
  const tasksByStageRef = useRef(tasksByStage);
  tasksByStageRef.current = tasksByStage;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor),
  );
  // Fetch assignees
  const fetchAssignees = useCallback(async () => {
    try {
      const res = await GetRequest(
        `users/users/fetchUserAsOwner/${customerId}`,
        { headers },
        extras,
      );
      if (res?.err) return;
      setAssigneeOptions(
        (res?.data || []).map((x) => ({ value: x.id, label: x.full_name })),
      );
    } catch (error) {
      console.error(error);
    }
  }, [customerId, headers, extras]);

  useEffect(() => {
    fetchAssignees();
  }, [fetchAssignees]);

  // Build stages from project data
  useEffect(() => {
    if (projectData?.stages) {
      const stageList = projectData.stages.map((stage) => ({
        id: stage.id,
        name: stage.stage_name || "Unnamed",
        progress: stage.taskCompletion_percentage || 0,
        color:
          stage.iconType === "orange"
            ? "#f1a545"
            : stage.iconType === "gray"
              ? "#87909e"
              : "#1bbf6b",
      }));
      setStages(stageList);

      const taskMap = {};
      projectData.stages.forEach((stage) => {
        taskMap[stage.id] = (stage.tasks || []).map((task) => ({
          ...task,
          project_stage_id: stage.id,
          stage_name: stage.stage_name,
          assignees: task.assignees || [],
        }));
      });
      setTasksByStage(taskMap);
    }
  }, [projectData,editedMode]);


  const findContainer = useCallback((id) => {
    if (!id) return null;
    if (tasksByStageRef.current[id]) return id;
    return (
      Object.keys(tasksByStageRef.current).find((stageId) =>
        tasksByStageRef.current[stageId].some((t) => t.id === id),
      ) || null
    );
  }, []);

  const handleDragStart = (event) => {
    const { active } = event;
    const container = findContainer(active.id);
    originalContainerRef.current = container;
    const task = tasksByStageRef.current[container]?.find(
      (t) => t.id === active.id,
    );
    setActiveTask(task || null);
    const stageIndex = stages.findIndex((s) => s.id === container);
    setActiveCardColor(stageIndex !== -1 ? getStageColor(stageIndex) : null);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer === overContainer
    ) {
      return;
    }

    setTasksByStage((prev) => {
      const activeItems = prev[activeContainer];
      const overItems = prev[overContainer];
      const activeIndex = activeItems.findIndex((t) => t.id === activeId);
      if (activeIndex === -1) return prev;
      const overIndex = overItems.findIndex((t) => t.id === overId);

      let newIndex;
      if (overId in prev) {
        newIndex = overItems.length;
      } else {
        const isBelowOverItem =
          over &&
          active.rect.current.translated &&
          active.rect.current.translated.top > over.rect.top + over.rect.height;
        const modifier = isBelowOverItem ? 1 : 0;
        newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length;
      }

      return {
        ...prev,
        [activeContainer]: activeItems.filter((t) => t.id !== activeId),
        [overContainer]: [
          ...overItems.slice(0, newIndex),
          activeItems[activeIndex],
          ...overItems.slice(newIndex),
        ],
      };
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);
    setActiveCardColor(null);
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;
    const sourceStageId = originalContainerRef.current;
    const destContainer = findContainer(overId) || findContainer(activeId);
    if (!sourceStageId || !destContainer) return;

    // Reordering within the same column – purely a client-side sort.
    if (sourceStageId === destContainer) {
      const items = tasksByStageRef.current[destContainer];
      const activeIndex = items.findIndex((t) => t.id === activeId);
      const overIndex = items.findIndex((t) => t.id === overId);
      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        setTasksByStage((prev) => ({
          ...prev,
          [destContainer]: arrayMove(
            prev[destContainer],
            activeIndex,
            overIndex,
          ),
        }));
      }
      return;
    }

    // Moved into a different column – persist the new status/stage.
    const taskData = tasksByStageRef.current[destContainer]?.find(
      (t) => t.id === activeId,
    );
    if (!taskData) return;
    const parseIfNeeded = (data) => {
        if (typeof data === "string") {
          try {
            return JSON.parse(data);
          } catch (e) {
            return data;
          }
        }
        return data;
      };
    try {
      const newStatus =
        stages.find((s) => s.id === destContainer)?.name?.toLowerCase() ||
        "open";
      const statusMap = { progress: "open", "to do": "progress" };
      const mappedStatus = statusMap[newStatus] || newStatus;
      const payload = {
        stage_id: destContainer,
        task_id: activeId,
        task_status: taskData?.task_status,
        task_name: taskData?.task_name,
        reason: "",
        project_id: projectData?.id,
        owner_id: normalizeOwnerIdPayload(
          taskData?.assignees?.length ? taskData.assignees : taskData?.owner_id,
          assigneeOptions,
        ),
        priority: taskData?.priority,
        start_date: taskData?.start_date,
        end_date: taskData?.end_date,
        attachment: taskData?.attachment || [],
        description: taskData?.description,
        tags:parseIfNeeded(taskData?.tags) || [],
      };
      payload.updating_field=getUpdatedFields(taskData,payload)
      const res = await PutRequest(
        "project/updateStageTask",
        payload,
        { headers },
        extras,
      );

      if (res.status === "fail") {
        toast.error(res.message);
        // Revert the optimistic move back to its source column.
        setTasksByStage((prev) => {
          const destItems = prev[destContainer].filter(
            (t) => t.id !== activeId,
          );
          const sourceItems = [...prev[sourceStageId], taskData];
          return {
            ...prev,
            [destContainer]: destItems,
            [sourceStageId]: sourceItems,
          };
        });
        return;
      }

      setTasksByStage((prev) => ({
        ...prev,
        [destContainer]: prev[destContainer].map((t) =>
          t.id === activeId
            ? {
                ...t,
                task_status: mappedStatus,
                project_stage_id: destContainer,
              }
            : t,
        ),
      }));

      toast.success("Task moved successfully");
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error(error);
      toast.error("Could not move task");
      setTasksByStage((prev) => {
        const destItems = prev[destContainer].filter((t) => t.id !== activeId);
        const sourceItems = [...prev[sourceStageId], taskData];
        return {
          ...prev,
          [destContainer]: destItems,
          [sourceStageId]: sourceItems,
        };
      });
    }
  };

  const handleUpdateField = async (taskId, updates) => {
    try {
      const task = Object.values(tasksByStage)
        .flat()
        .find((t) => t.id === taskId);
      if (!task) return;
      const parseIfNeeded = (data) => {
        if (typeof data === "string") {
          try {
            return JSON.parse(data);
          } catch (e) {
            return data;
          }
        }
        return data;
      };
      const payload = {
        task_id: taskId,
        project_id: task.project_id,
        stage_id: task.project_stage_id,
        task_name: task.task_name,
        task_status: task.task_status,
        description: task?.description || "",
        priority:
          updates.priority !== undefined ? updates.priority : task.priority,
        owner_id: normalizeOwnerIdPayload(
          updates.assignees !== undefined
            ? updates.assignees
            : task?.assignees?.length
              ? task.assignees
              : task?.owner_id,
          assigneeOptions,
        ),
        start_date:
          updates.start_date !== undefined
            ? updates.start_date
            : task.start_date || todayISO(),
        // due_date:
        //   updates.due_date !== undefined
        //     ? updates.due_date
        //     : hasRealDate(task.end_date)
        //       ? task.end_date
        //       : task.due_date,
        end_date:
          updates.due_date !== undefined
            ? updates.due_date
            : hasRealDate(task.end_date)
              ? task.end_date
              : task.due_date || tomorrowISO(),
        tags:parseIfNeeded(task?.tags) || [],      
      };

      payload.updating_field=getUpdatedFields(task,payload)
      const res = await PutRequest(
        "project/updateStageTask",
        payload,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        toast.error(res?.message || "Could not update task");
        return;
      }
      const normalizedUpdates = { ...updates };
      if (normalizedUpdates.due_date !== undefined) {
        normalizedUpdates.end_date = normalizedUpdates.due_date;
      }
      if (normalizedUpdates.assignees !== undefined) {
        normalizedUpdates.assignees = normalizeOwnerIdPayload(
          normalizedUpdates.assignees,
          assigneeOptions,
        );
        normalizedUpdates.owner_id = normalizedUpdates.assignees;
      }

      const newTasksByStage = { ...tasksByStage };
      Object.keys(newTasksByStage).forEach((stageId) => {
        newTasksByStage[stageId] = newTasksByStage[stageId].map((t) => {
          if (t.id === taskId) {
            return { ...t, ...normalizedUpdates };
          }
          return t;
        });
      });
      setTasksByStage(newTasksByStage);

      if (onDataChange) onDataChange();
    } catch (error) {
      console.error(error);
      toast.error("Could not update task");
    }
  };

  const handleAddTask = (columnId) => {
    setSelectedColumn(columnId);
    setShowAddModal(true);
  };

  const handleQuickAddTask = async (
    columnId,
    taskName,
    assignees,
    startDate,
    dueDate,
    priority,
  ) => {
    try {
      const payload = {
        project_id: projectData?.id,
        project_stage_id: columnId,
        task_name: taskName,
        description: "",
        task_status: "open",
        priority: priority || "normal",
        owner_id: normalizeOwnerIdPayload(assignees, assigneeOptions),
        start_date: startDate || todayISO(),
        end_date: dueDate || tomorrowISO(),
      };

      const res = await PostRequest(
        "project/createTask",
        payload,
        false,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        toast.error(res?.message || "Could not create task");
        return;
      }

      const newTask = {
        ...res.data,
        project_stage_id: columnId,
        assignees: res.data?.assignees || [],
      };

      setTasksByStage((prev) => ({
        ...prev,
        [columnId]: [...(prev[columnId] || []), newTask],
      }));

      if (onDataChange) onDataChange();
    } catch (error) {
      console.error(error);
      toast.error("Could not create task");
    }
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setShowEditModal(true);
  };

  const handleDeleteTask = async (task) => {
    if (
      !window.confirm(
        `Are you sure you want to delete task "${task.task_name}"?`,
      )
    )
      return;

    try {
      const res = await PostRequest(
        `project/deleteTask/${task.id}`,
        {},
        false,
        { headers },
        extras,
      );
      if (res?.status === "success") {
        const newTasksByStage = { ...tasksByStage };
        Object.keys(newTasksByStage).forEach((stageId) => {
          newTasksByStage[stageId] = newTasksByStage[stageId].filter(
            (t) => t.id !== task.id,
          );
        });
        setTasksByStage(newTasksByStage);
        toast.success("Task deleted successfully");
        if (onDataChange) onDataChange();
      } else {
        toast.error(res?.message || "Could not delete task");
      }
    } catch (error) {
      console.error(error);
      toast.error("Could not delete task");
    }
  };

  const handleStatusChange = async (task, newStatus, reason) => {
    try {
      const payload = {
        stage_id: task.project_stage_id,
        task_id: task.id,
        task_status: newStatus,
        reason: reason || "",
      };

      const res = await PutRequest(
        "project/changeTaskStatus",
        payload,
        { headers },
        extras,
      );

      if (!res || res.status === "fail") {
        toast.error(res?.message || "Could not update task status");
        return false;
      }

      const newTasksByStage = { ...tasksByStage };
      Object.keys(newTasksByStage).forEach((stageId) => {
        newTasksByStage[stageId] = newTasksByStage[stageId].map((t) =>
          t.id === task.id ? { ...t, task_status: newStatus } : t,
        );
      });
      setTasksByStage(newTasksByStage);
      toast.success("Task status updated successfully");
      if (onDataChange) onDataChange();
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Could not update task status");
      return false;
    }
  };

  const handleTaskCreated = (newTask) => {
    const newTasksByStage = { ...tasksByStage };
    newTasksByStage[selectedColumn] = [
      ...newTasksByStage[selectedColumn],
      newTask,
    ];
    setTasksByStage(newTasksByStage);
    if (onDataChange) onDataChange();
  };

  const handleTaskUpdated = (updatedTask) => {
    const newTasksByStage = { ...tasksByStage };
    Object.keys(newTasksByStage).forEach((stageId) => {
      newTasksByStage[stageId] = newTasksByStage[stageId].map((t) =>
        t.id === updatedTask.id
          ? { ...updatedTask, project_stage_id: t.project_stage_id }
          : t,
      );
    });
    setTasksByStage(newTasksByStage);
    if (onDataChange) onDataChange();
  };

  const getColumnColor = (columnName) => {
    const name = columnName?.toLowerCase() || "";
    if (name.includes("todo") || name.includes("to do")) return "#87909e";
    if (name.includes("progress")) return "#f1c232";
    if (name.includes("complete") || name.includes("done")) return "#1bbf6b";
    if (name.includes("review")) return "#4592f0";
    if (name.includes("close") || name.includes("cancelled")) return "#e5484d";
    return "#7B68EE";
  };

  const totalTasks = Object.values(tasksByStage).reduce(
    (acc, tasks) => acc + tasks.length,
    0,
  );

    const handleProjectEditClicked = (project) => {
    setProject_id(project?.id);
    setEditMode(true)
    setProjectOffcanvas(true);
  };

  return (
    <>
      <div className="kb-wrap">
        <div className="kb-header">
          <div className="kb-header-left">
            <h5 className="kb-title">Board</h5>
            <span className="kb-task-count">{totalTasks} tasks</span>
          </div>
          <div className="kb-header-actions">
            <button
              className="kb-btn-outline"
              onClick={() => handleProjectEditClicked(projectData)}
            >
             Edit Project
            </button>
          </div>
        </div>

        <div className="kb-scroll-container">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="kb-columns">
              {stages.map((stage, stageIndex) => (
                <KanbanColumn
                  key={stage.id}
                  column={stage}
                  tasks={tasksByStage[stage.id] || []}
                  onQuickAddTask={handleQuickAddTask}
                  onTaskClick={onTaskClick}
                  onEdit={handleEditTask}
                  onDelete={handleDeleteTask}
                  taskGrant={taskGrant}
                  onStatusChange={handleStatusChange}
                  onUpdateField={handleUpdateField}
                  assigneeOptions={assigneeOptions}
                  color={stage.color || getColumnColor(stage.name)}
                  cardColor={getStageColor(stageIndex)}
                />
              ))}
            </div>
            <DragOverlay
              dropAnimation={{
                duration: 180,
                easing: "cubic-bezier(0.2, 0, 0, 1)",
              }}
            >
              {activeTask ? (
                <KanbanTaskCardPreview
                  task={activeTask}
                  taskGrant={taskGrant}
                  cardColor={activeCardColor}
                  assigneeOptions={assigneeOptions}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      <AddTaskModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSubmit={handleTaskCreated}
        columnId={selectedColumn}
        assigneeOptions={assigneeOptions}
        projectData={projectData}
        taskGrant={taskGrant}
        headers={headers}
        extras={extras}
      />

      <EditTaskModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        task={selectedTask}
        onSubmit={handleTaskUpdated}
        assigneeOptions={assigneeOptions}
        headers={headers}
        taskGrant={taskGrant}
        extras={extras}
      />
      {showProjectCanvas && (
        <CreateProject
          show={showProjectCanvas}
          setProjectOffcanvas={setProjectOffcanvas}
          editedMode={editedMode}
          setEditMode={setEditMode}
          project_id={project_id}
          setProject_id={setProject_id}
          fetchProjectList={fetchProjectList}
          refetch={refetch}
        />
      )}
      <style>{`
        /* (styles unchanged except z-index for popovers) */
        .kb-wrap {
          padding: 16px 20px;
          background: #f5f6fa;
          min-height: calc(100vh - 100px);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .kb-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 4px 16px 4px;
          border-bottom: 1px solid #e0e4f8;
          margin-bottom: 16px;
          flex-shrink: 0;
        }
        .kb-header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .kb-title {
          margin: 0;
          font-weight: 600;
          font-size: 18px;
          color: #1a1a2e;
        }
        .kb-task-count {
          font-size: 13px;
          color: #6b7280;
          background: #e9ecf5;
          padding: 4px 12px;
          border-radius: 12px;
        }
        .kb-header-actions {
          display: flex;
          gap: 8px;
        }
        .kb-btn-outline {
          padding: 6px 16px;
          font-size: 13px;
          border: 1px solid #d0d7de;
          border-radius: 6px;
          background: white;
          color: #24292f;
          cursor: pointer;
          transition: all 0.2s;
        }
        .kb-btn-outline:hover {
          background: #f6f8fa;
        }
        .kb-scroll-container {
          flex: 1;
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 16px;
          min-height: 0;
        }
        .kb-scroll-container::-webkit-scrollbar {
          height: 8px;
        }
        .kb-scroll-container::-webkit-scrollbar-track {
          background: #e9ecf5;
          border-radius: 4px;
        }
        .kb-scroll-container::-webkit-scrollbar-thumb {
          background: #d0d7de;
          border-radius: 4px;
        }
        .kb-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #b0b7be;
        }
        .kb-columns {
          display: flex;
          gap: 2px;
          padding-bottom: 8px;
          align-items: stretch;
          min-width: max-content;
          height: 100%;
        }
        .kb-column {
          min-width: 300px;
          max-width: 300px;
          border-radius: 12px;
          padding: 0px;
          display: flex;
          flex-direction: column;
          height: 100%;
          max-height: calc(100vh - 220px);
          flex-shrink: 0;
        }
        .kb-column-header {
          display: flex;
          justify-content: space-between;
          border: 1px solid rgb(227, 227, 227);
          border-radius: 4px;
          background: rgb(255, 255, 255);
          align-items: center;
          padding: 11px 17px;
          flex-shrink: 0;
        }
        .kb-column-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .kb-column-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          display: inline-block;
        }
        .kb-column-name {
          font-weight: 600;
          font-size: 14px;
          color: #1a1a2e;
        }
        .kb-column-count {
          background: #e9ecf5;
          color: #4a4a5a;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }
        .kb-column-add-btn {
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          border: 1px solid #d0d7de;
          border-radius: 50%;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
        }
        .kb-column-add-btn:hover {
          background: #4592f0;
          border-color: #4592f0;
          color: #fff;
        }
        .kb-column-tasks {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          padding: 8px 4px 0 4px;
          min-height: 100px;
          transition: background-color 0.2s;
        }
        .kb-column-tasks::-webkit-scrollbar {
          width: 4px;
        }
        .kb-column-tasks::-webkit-scrollbar-thumb {
          background: #d0d7de;
          border-radius: 4px;
        }
        .kb-column-tasks::-webkit-scrollbar-track {
          background: transparent;
        }
        .kb-drag-over {
          background: rgba(69, 146, 240, 0.06);
          border-radius: 8px;
        }
        .kb-task-card {
        opacity: 1;
    z-index: auto;
    background-color: rgb(216, 248, 255);
    border-radius: 10px;
    padding: 12px;
    width: 100%;
    box-shadow: rgba(0, 0, 0, 0.08) 0px 2px 6px;
    user-select: none;
    position: relative;
    border: 1px solid rgb(221, 221, 221);
    will-change: transform;
    backface-visibility: hidden;
    margin-bottom: 8px;
        }
        .kb-task-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-1px);
        }
        .kb-task-card:active {
          cursor: grabbing;
        }
        .kb-task-card-overlay {
          cursor: grabbing;
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
          transform: rotate(2deg);
          width: 272px;
        }
        .kb-task-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 6px;
        }
        .kb-task-title {
          font-size: 14px;
          font-weight: 500;
          color: #1a1a2e;
          flex: 1;
          line-height: 1.4;
        }
        .kb-task-title-clickable {
          cursor: pointer;
        }
        .kb-task-title-clickable:hover {
          text-decoration: underline;
          color: #4592f0;
        }
        .kb-task-actions {
          flex-shrink: 0;
        }
        .kb-task-more-btn {
          padding: 2px 4px;
          color: #6b7280;
          background: transparent;
          border: none;
          line-height: 1;
        }
        .kb-task-more-btn:hover {
          color: #1a1a2e;
          background: #f1f3f5;
          border-radius: 4px;
        }
        .kb-task-description {
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 8px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.3;
        }
        .kb-task-meta {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }
        .kb-task-priority {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          padding: 2px 6px;
          border-radius: 4px;
          transition: background 0.2s;
        }
        .kb-task-priority:hover {
          background: #f1f3f5;
        }
        .kb-task-due {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #6b7280;
          cursor: pointer;
          padding: 2px 6px;
          border-radius: 4px;
          transition: background 0.2s;
        }
        .kb-task-due:hover {
          background: #f1f3f5;
        }
        .kb-task-overdue {
          color: #e5484d;
        }
        .kb-task-status {
          font-size: 10px;
          font-weight: 600;
          padding: 2px 8px;
          border: 1px solid;
          border-radius: 4px;
          letter-spacing: 0.3px;
        }
        .kb-task-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 8px;
          border-top: 1px solid #f1f3f5;
        }
        .kb-task-assignees {
          display: flex;
          align-items: center;
          gap: 2px;
          cursor: pointer;
          padding: 2px 4px;
          border-radius: 4px;
          transition: background 0.2s;
        }
        .kb-task-assignees:hover {
          background: #f1f3f5;
        }
        .kb-no-assignee {
          font-size: 11px;
          color: #9aa0b0;
          display: flex;
          align-items: center;
        }
        .kb-more-assignees {
          font-size: 10px;
          color: #6b7280;
          background: #f1f3f5;
          padding: 1px 6px;
          border-radius: 10px;
        }
        .kb-avatar {
          border-radius: 50%;
          color: #fff;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 2px solid white;
          margin-left: -4px;
        }
        .kb-avatar:first-child {
          margin-left: 0;
        }
        .kb-avatar-img {
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
          border: 2px solid white;
          margin-left: -4px;
        }
        .kb-avatar-img:first-child {
          margin-left: 0;
        }
        .kb-task-stats {
          display: flex;
          gap: 8px;
        }
        .kb-task-stat {
          display: flex;
          align-items: center;
          gap: 3px;
          font-size: 11px;
          color: #6b7280;
        }
        .kb-empty-tasks {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 36px 12px 24px;
          text-align: center;
          gap: 10px;
        }
        .kb-empty-title {
          color: #8a90a3;
          font-size: 13px;
          font-weight: 500;
        }
        .kb-empty-add-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: transparent;
          border: none;
          color: #4592f0;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          padding: 4px 10px;
          border-radius: 6px;
          transition: background 0.2s;
        }
        .kb-empty-add-btn:hover {
          background: #eef4fd;
        }
        .kb-add-task-bottom {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #6b7280;
          font-size: 13px;
          padding: 6px 10px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 4px;
          background: #e9ecf5;
          min-height:45px;
        }
        .kb-add-task-bottom:hover {
          background: #e9ecf5;
          color: #1a1a2e;
        }
        .kb-quick-add {
          background: #fff;
          border-radius: 8px;
          padding: 10px;
          margin-top: 4px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
          border: 1px solid #4592f0;
        }
        .kb-quick-add-input {
          width: 100%;
          border: none;
          outline: none;
          resize: none;
          font-size: 13px;
          font-family: inherit;
          color: #1a1a2e;
          padding: 2px;
        }
        .kb-quick-add-toolbar {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 6px;
          flex-wrap: wrap;
        }
        .kb-quick-add-tool {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #6b7280;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: background 0.2s;
          background: #f5f7fa;
          border: 1px solid #e0e4f8;
        }
        .kb-quick-add-tool:hover {
          background: #e9ecf5;
        }
        .kb-quick-add-tool-label {
          font-weight: 500;
          color: #1a1a2e;
        }
        .kb-quick-add-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
        }
        .kb-quick-add-submit {
          background: #4592f0;
          color: #fff;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          padding: 6px 14px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .kb-quick-add-submit:hover:not(:disabled) {
          background: #2f7ad6;
        }
        .kb-quick-add-submit:disabled {
          background: #b7cdf0;
          cursor: not-allowed;
        }
        .kb-quick-add-cancel {
          background: transparent;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 4px 6px;
          border-radius: 4px;
        }
        .kb-quick-add-cancel:hover {
          background: #f1f3f5;
          color: #1a1a2e;
        }
        .kb-popover-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9998; /* below popover */
          background: rgba(0,0,0,0.1);
        }
        .kb-popover {
          position: fixed;
          z-index: 9999; /* above everything */
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 8px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.2);
          min-width: 220px;
          padding: 6px;
          animation: kbPopoverIn 0.15s ease;
          max-height: 80vh;
          overflow-y: auto;
        }
        @keyframes kbPopoverIn {
          from { opacity: 0; transform: scale(0.95) translateY(-4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .kb-popover-search {
          width: 100%;
          background: #f5f5f5;
          border: 1px solid #ccc;
          border-radius: 6px;
          color: #1a1a1e;
          font-size: 13px;
          padding: 6px 10px;
          margin-bottom: 6px;
          outline: none;
        }
        .kb-popover-search:focus { border-color: #7B3FE4; }
        .kb-popover-section-label {
          font-size: 10px;
          color: #666;
          padding: 4px 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .kb-popover-list { max-height: 220px; overflow-y: auto; }
        .kb-popover-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .kb-popover-row:hover { background: #f0f0f0; }
        .kb-popover-row-active { background: #e0ecff; }
        .kb-popover-empty {
          padding: 8px;
          color: #666;
          font-size: 12px;
        }
        .kb-check { margin-left: auto; color: #4592f0; }
        .kb-assignee-popover { width: 260px; }
        .kb-option-popover { min-width: 160px; }
        .kb-date-popover {
          width: 560px;
          padding: 0;
          overflow: hidden;
        }
        .kb-date-tabs {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-bottom: 1px solid #ddd;
          background: #f5f5f5;
        }
        .kb-date-tab {
          background: #fff;
          border: 1px solid #ccc;
          color: #555;
          border-radius: 6px;
          font-size: 12px;
          padding: 6px 14px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .kb-date-tab:hover { border-color: #aaa; }
        .kb-date-tab-active {
          border-color: #4592f0;
          color: #000;
          background: #e0ecff;
        }
        .kb-date-body { display: flex; }
        .kb-date-quick {
          width: 160px;
          border-right: 1px solid #ddd;
          padding: 6px;
        }
        .kb-date-quick-row {
          display: flex;
          justify-content: space-between;
          padding: 7px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          transition: background 0.15s;
        }
        .kb-date-quick-row:hover { background: #f0f0f0; }
        .kb-date-recurring {
          border-top: 1px solid #ddd;
          margin-top: 4px;
          padding-top: 10px;
          color: #666;
        }
        .kb-date-calendar { flex: 1; padding: 10px 16px; }
        .kb-date-cal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }
        .kb-cal-nav {
          cursor: pointer;
          color: #666;
          padding: 2px 8px;
          font-size: 16px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .kb-cal-nav:hover {
          color: #000;
          background: #f0f0f0;
        }
        .kb-date-cal-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          text-align: center;
        }
        .kb-date-cal-dow {
          color: #666;
          font-size: 11px;
          margin-bottom: 4px;
          padding: 4px 0;
        }
        .kb-cal-cell {
          padding: 6px 0;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.15s;
        }
        .kb-cal-cell:hover { background: #f0f0f0; }
        .kb-cal-cell-empty { cursor: default; }
        .kb-cal-cell-empty:hover { background: transparent; }
        .kb-cal-cell-today {
          color: #f1c232;
          font-weight: 700;
          border: 1px solid #f1c232;
        }
        .kb-cal-cell-selected {
          background: #4592f0;
          color: #fff;
          border-radius: 50%;
        }
        .kb-muted { color: #6b6b72; }
        .fs-11 { font-size: 11px; }
        .fs-13 { font-size: 13px; }
        .fw-semibold { font-weight: 600; }
        .d-flex { display: flex; }
        .gap-2 { gap: 8px; }
        .flex-grow-1 { flex: 1; }
        .kb-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }

        .kb-status-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(20, 20, 24, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }
        .kb-status-modal {
          width: 420px;
          max-width: 92vw;
          background: #fff;
          border-radius: 10px;
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }
        .kb-status-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          font-size: 15px;
          font-weight: 600;
          color: #1a1a1e;
          border-bottom: 1px solid #eee;
        }
        .kb-status-modal-close {
          cursor: pointer;
          color: #888;
          display: inline-flex;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .kb-status-modal-close:hover {
          color: #1a1a1e;
          background: #f0f0f0;
        }
        .kb-status-modal-body {
          padding: 18px 20px 6px;
        }
        .kb-status-modal-message {
          margin: 0 0 12px;
          font-size: 13px;
          color: #1a1a1e;
        }
        .kb-status-modal-label {
          display: block;
          font-size: 12px;
          color: #666;
          margin-bottom: 6px;
        }
        .kb-status-modal-textarea {
          width: 100%;
          min-height: 90px;
          resize: vertical;
          border: 1px solid #ccc;
          border-radius: 6px;
          padding: 10px;
          font-size: 13px;
          color: #1a1a1e;
          font-family: inherit;
        }
        .kb-status-modal-textarea:focus {
          outline: none;
          border-color: #4592f0;
        }
        .kb-status-modal-textarea::placeholder { color: #999; }
        .kb-status-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 16px 20px;
        }
        .kb-status-modal-cancel {
          font-size: 12px;
          border-radius: 6px;
          padding: 5px 14px;
          border: 1px solid #ccc;
          background: transparent;
          color: #1a1a1e;
          cursor: pointer;
          transition: all 0.2s;
        }
        .kb-status-modal-cancel:hover {
          background: #f0f0f0;
          border-color: #aaa;
        }
        .kb-status-modal-submit {
          font-size: 12px;
          border-radius: 6px;
          padding: 5px 16px;
          border: 1px solid #a13b58;
          background: #a13b58;
          color: #fff;
          cursor: pointer;
          transition: all 0.2s;
        }
        .kb-status-modal-submit:hover {
          background: #8c3249;
        }
        .kb-status-modal-submit:disabled,
        .kb-status-modal-cancel:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        /* Modal Styles */
        .kb-add-task-modal .modal-content {
          border-radius: 12px;
          border: none;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }
        .kb-add-task-modal .modal-header {
          border-bottom: 1px solid #e9ecf5;
          padding: 16px 24px;
        }
        .kb-add-task-modal .modal-header .modal-title {
          font-weight: 600;
          font-size: 18px;
        }
        .kb-add-task-modal .modal-body {
          padding: 24px;
        }
        .kb-add-task-modal .modal-footer {
          border-top: 1px solid #e9ecf5;
          padding: 16px 24px;
        }
        @media (max-width: 768px) {
          .kb-column {
            min-width: 260px;
            max-width: 260px;
          }
          .kb-header {
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
          }
          .kb-date-popover {
            width: 320px;
          }
          .kb-date-body {
            flex-direction: column;
          }
          .kb-date-quick {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid #ddd;
          }
        }
          .tl-date-popover-close {
          margin-left: auto;
          cursor: pointer;
          color: #888;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px;
          border-radius: 4px;
          transition: all 0.2s;
        } 
        .tl-date-popover-close:hover {
          color: #1a1a1e;
          background: #e8e8e8;
        }
       .tl-date-tab:hover { border-color: #aaa; }
      `}</style>
    </>
  );
};

export default KanbanBoard;